'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { ProductLogoMediumEquipment } from '@/components/Logo';
import { Text } from '@/components/Typography';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { ledgerColumn } from '../-shared/-components/ledgerColumn';
import { LedgerContent } from '../-shared/-components/LedgerContent';
import { LedgerDataTable } from '../-shared/-components/LedgerDataTable';
import { isForbiddenError, LedgerForbiddenState } from '../-shared/-components/LedgerForbiddenState';
import { LedgerHeader } from '../-shared/-components/LedgerHeader';
import { Field, FieldGrid, ResourceDetailCard } from '../-shared/-components/ResourceDetailCard';
import { useEquipmentLedger } from '../-shared/-query';
import { EquipmentStatusBadge } from '../../../../-components/EquipmentStatusBadge';
const EQUIPMENTS_PATH = '/individual/portal/lookup/equipments';
const LOOKUP_PATH = '/individual/portal/lookup';
const equipmentsSearchSchema = z.object({
  scope: z.enum(['self']).catch('self'),
  id: z.string().optional(),
  pageIndex: z.coerce.number().int().min(0).catch(0),
  pageSize: z.coerce.number().int().min(1).max(100).catch(20)
});
export const Route = createFileRoute('/_individual/individual/portal/lookup/_ledger/equipments/')({
  validateSearch: equipmentsSearchSchema,
  component: EquipmentsLookupPage
});
type EquipmentLedgerItem = NonNullable<ReturnType<typeof useEquipmentLedger>['data']>['items'][number];
function EquipmentsLookupPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.lookupResources'
  });
  const {
    t: ct
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.breadcrumbs'
  });
  const {
    t: th
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home'
  });
  const {
    scope,
    id,
    pageIndex,
    pageSize
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [freeText, setFreeText] = useState('');
  const debouncedSetFreeText = useDebouncedCallback((text: string) => {
    setFreeText(text);
    // 検索語変更時は 1 ページ目に戻す（存在しないページで空表示になるのを防ぐ）。
    void navigate({
      search: prev => ({
        ...prev,
        pageIndex: 0
      })
    });
  }, []);
  const {
    data,
    isPending,
    isError,
    error
  } = useEquipmentLedger({
    page: pageIndex + 1,
    pageSize,
    scope,
    freeText: freeText === '' ? undefined : freeText
  });
  const selected = id != null ? data?.items.find(e => e.id === id) ?? null : null;
  const statusLabel = (status: EquipmentLedgerItem['status']) => t(`equipments.status.${status.toLowerCase()}` as const);
  if (isError && isForbiddenError(error)) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('equipments.title')
      }]} Logo={ProductLogoMediumEquipment} title={t('equipments.title')} />
        <LedgerContent>
          <LedgerForbiddenState onBack={() => void navigate({
          to: LOOKUP_PATH
        })} />
        </LedgerContent>
      </>;
  }
  if (isError) {
    throw error;
  }
  if (selected != null) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('equipments.title'),
        link: {
          to: EQUIPMENTS_PATH,
          search: {
            scope,
            pageIndex,
            pageSize
          }
        }
      }, {
        label: selected.name
      }]} Logo={ProductLogoMediumEquipment} title={selected.name} subtitle={selected.assetTag} />
        <LedgerContent>
          <ResourceDetailCard title={t('equipments.detail.basicInfo')}>
            <FieldGrid>
              <Field label={t('equipments.detail.status')} value={<EquipmentStatusBadge status={selected.status} label={statusLabel(selected.status)} />} />
              <Field label={t('equipments.detail.equipmentTypeName')} value={selected.equipmentTypeName ?? '—'} />
              <Field label={t('equipments.detail.manufacturer')} value={selected.manufacturer ?? '—'} />
              <Field label={t('equipments.detail.productName')} value={selected.productName ?? '—'} />
              <Field label={t('equipments.detail.modelNumber')} value={selected.modelNumber ?? '—'} />
              <Field label={t('equipments.detail.assetTag')} value={selected.assetTag ?? '—'} />
              <Field label={t('equipments.detail.serialNumber')} value={selected.serialNumber ?? '—'} />
            </FieldGrid>
          </ResourceDetailCard>
        </LedgerContent>
      </>;
  }
  const columns = [ledgerColumn<EquipmentLedgerItem>({
    id: 'name',
    header: t('equipments.headers.name'),
    cell: e => <Text variant="label">{e.name}</Text>
  }), ledgerColumn<EquipmentLedgerItem>({
    id: 'equipmentTypeName',
    header: t('equipments.headers.equipmentTypeName'),
    cell: e => e.equipmentTypeName ?? '—'
  }), ledgerColumn<EquipmentLedgerItem>({
    id: 'manufacturer',
    header: t('equipments.headers.manufacturer'),
    cell: e => e.manufacturer ?? e.productName ?? '—'
  }), ledgerColumn<EquipmentLedgerItem>({
    id: 'modelNumber',
    header: t('equipments.headers.modelNumber'),
    cell: e => e.modelNumber ?? '—'
  }), ledgerColumn<EquipmentLedgerItem>({
    id: 'assetTag',
    header: t('equipments.headers.assetTag'),
    cell: e => e.assetTag ?? '—'
  }), ledgerColumn<EquipmentLedgerItem>({
    id: 'status',
    header: t('equipments.headers.status'),
    cell: e => <EquipmentStatusBadge status={e.status} label={statusLabel(e.status)} />
  })];
  return <>
      <LedgerHeader breadcrumbs={[{
      label: ct('home'),
      link: {
        to: '/'
      }
    }, {
      label: th('lookup.title'),
      link: {
        to: LOOKUP_PATH
      }
    }, {
      label: t('equipments.title')
    }]} Logo={ProductLogoMediumEquipment} title={t('equipments.title')} />
      <LedgerContent className="h-full">
        <LedgerDataTable<EquipmentLedgerItem> fillHeight manualPagination columns={columns} data={data?.items} isPending={isPending} pageCount={data?.maxPage ?? 0} totalCount={data?.count} pagination={{
        pageIndex,
        pageSize
      }} onPaginationChange={updater => {
        const next = typeof updater === 'function' ? updater({
          pageIndex,
          pageSize
        }) : updater;
        void navigate({
          search: prev => ({
            ...prev,
            pageIndex: next.pageIndex,
            pageSize: next.pageSize
          })
        });
      }} onSearchTextChange={debouncedSetFreeText} getRowId={e => e.id} searchPlaceholder={t('common.search')} onRowSelect={e => void navigate({
        search: prev => ({
          ...prev,
          id: e.id
        })
      })} emptyContent={<Text variant="p" className="text-neutral-muted-on-fill">
              {t('common.empty')}
            </Text>} />
      </LedgerContent>
    </>;
}